import { ChangeEvent, FC, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { AlertType, appendAlertItem, authenticateFailed, logout } from '@pruforce/common-adminweb-sdk';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { isPermitted, toAbsoluteUrl, useJWT } from 'src/app/common/utils';
import { map } from 'lodash';
import { AdminCommissionToolPermissionType, ErrorCodes } from '../../../constants';
import { importContractSettingCsv } from '../../../network/contract-setting-crud';
import { contractSettingPath } from '../contract-setting-routes';
import { useTranslation } from 'src/app/common/hook/translation.hook';
import { validateImportFile } from '../../../utils/import-validation';
import { ApiErrorResponse, ErrorType } from 'src/app/common/network';

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  section: {
    marginBottom: 16,
  },
  label: {
    fontSize: '1rem',
    marginBottom: 24,
  },
  fileName: {
    marginTop: 8,
    color: theme.palette.text.secondary,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
  },
  mandatory: {
    color: 'red',
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  uploadRowContainer: {
    width: '100%',
    display: 'flex',
  },
  fieldContainer: {
    width: 'auto',
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  errorText: {
    fontSize: 9,
    color: '#F018A6',
  },
}));

const ContractSettingImportPage: FC = () => {
  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();
  const Translation = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedFile, setSelectedFile] = useState<File>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorLineModalOpen, setErrorLineModalOpen] = useState(false);
  const [errorLineMessage, setErrorLineMessage] = useState('');
  const [fileError, setFileError] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);
  const [isSelectingFile, setIsSelectingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const latestSelectedFileRef = useRef<File | undefined>(undefined);
  const chooseFileSeqRef = useRef(0);

  let createPermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    createPermission = isPermitted([AdminCommissionToolPermissionType.CONTRACT_SETTING_CREATE], jwtPermission);
  }

  if (!createPermission) {
    history.push(`${contractSettingPath}/list`);
  }

  const onChooseFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const requestId = ++chooseFileSeqRef.current;
    setIsSelectingFile(true);
    latestSelectedFileRef.current = undefined;
    setSelectedFile(undefined);
    setFileError(undefined);

    const file = e.target.files?.[0];
    if (!file) {
      if (requestId === chooseFileSeqRef.current) {
        setIsSelectingFile(false);
      }
      return;
    }

    try {
      const snapshotBuffer = await file.arrayBuffer();
      const snapshotFile = new File([snapshotBuffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });

      if (requestId !== chooseFileSeqRef.current) {
        return;
      }

      const error = await validateImportFile(snapshotFile);

      if (error) {
        latestSelectedFileRef.current = undefined;
        setSelectedFile(undefined);
        setFileError(error);
        e.target.value = '';
        return;
      }

      latestSelectedFileRef.current = snapshotFile;
      setSelectedFile(snapshotFile);
      setFileError(undefined);
    } finally {
      if (requestId === chooseFileSeqRef.current) {
        setIsSelectingFile(false);
      }
    }
  };

  const onClickChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = toAbsoluteUrl('/templates/Template_Commission_Contract.csv');
    link.download = 'Template_Commission_Contract.csv';
    link.click();
  };

  const onSubmitConfirm = async () => {
    if (isSelectingFile) {
      return;
    }

    const error = await validateImportFile(latestSelectedFileRef.current);
    if (error) {
      setFileError(error);
      return;
    }

    setConfirmOpen(true);
  };

  const onSubmit = async () => {
    const latestFile = latestSelectedFileRef.current;
    if (!latestFile) {
      return;
    }

    setConfirmOpen(false);
    setIsImporting(true);
    try {
      await importContractSettingCsv(latestFile);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: Translation('master.data.setting.update.successNotName'),
          },
        ]),
      );
      history.push(`${contractSettingPath}/list`);
    } catch (err: unknown) {
      const error =
        (err as ApiErrorResponse) ||
        ({
          statusCode: 503,
          message: 'Network failed',
          code: 'Network failed',
          errors: [],
        } as ApiErrorResponse);

      if (error.code === ErrorCodes['Generic-3']) {
        setErrorLineMessage(error.message);
        setErrorLineModalOpen(true);
      } else {
        const forceLogout = error.code === ErrorType.AUTH_TOKEN_NOT_FOUND;
        if (forceLogout) {
          dispatch(authenticateFailed());
          dispatch(logout());
        } else {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: error.message,
              },
            ]),
          );
        }
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle align="center">{Translation('contract.setting.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{Translation('contract.setting.submit.confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={() => setConfirmOpen(false)}>
            {Translation('app.button.cancel')}
          </Button>
          <Button variant="contained" color="secondary" onClick={onSubmit}>
            {Translation('app.button.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={errorLineModalOpen} onClose={() => setErrorLineModalOpen(false)}>
        <DialogTitle align="center">{Translation('contract.setting.importErrorTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorLineMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" color="secondary" onClick={() => setErrorLineModalOpen(false)}>
            {Translation('contract.setting.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={isImporting} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={commonClasses.header}>{Translation('contract.setting.import')}</div>
          <Button
            variant="contained"
            color="inherit"
            disabled={isImporting}
            onClick={() => history.push(`${contractSettingPath}/list`)}
          >
            {Translation('app.button.back')}
          </Button>
        </div>

        <div className={classes.section}>
          <div className={classes.label}>{Translation('contract.setting.importDescription')}</div>
          <a
            href="#"
            className={commonClasses.link}
            style={{
              color: 'blue',
            }}
            onClick={(event) => {
              event.preventDefault();
              onDownloadTemplate();
            }}
          >
            {Translation('contract.setting.downloadTemplate')}
          </a>
        </div>

        <div style={{ marginBottom: 10 }} className={classes.uploadRowContainer}>
          <div style={{ paddingTop: 3 }} className={classes.fieldContainer}>
            <span className={classes.field}>
              {Translation('contract.setting.selectFile')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <input
              ref={fileInputRef}
              id="upload-default-target-csv"
              hidden
              type="file"
              accept=".csv"
              onChange={onChooseFile}
            />
            <div className={classes.rowContainer}>
              <Button
                variant="contained"
                color="secondary"
                disabled={isImporting || isSelectingFile}
                onClick={onClickChooseFile}
              >
                {Translation('select.label')}
              </Button>
              <div style={{ marginLeft: 10 }}>
                {selectedFile
                  ? `${selectedFile.name} - ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                  : Translation('contract.setting.noFileChosen')}
              </div>
              {fileError && (
                <span style={{ marginLeft: 10 }} className={classes.errorText}>
                  {Translation(fileError)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={classes.footerContainer}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onSubmitConfirm}
            disabled={isImporting || isSelectingFile}
          >
            {isImporting ? <CircularProgress size={24} color="inherit" /> : Translation('app.button.submit')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ContractSettingImportPage;
